import { useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api } from "../lib/api";
import { MEALS, fmt, num, run } from "../lib/util";
import { Badge, Btn, ProductSelect, cellCls } from "../components/kit";
import { useSchool } from "./ctx";

const BASIS = { ficha: ["Ficha", "green"], substituicao: ["Substituição", "info"], estimado: ["Modo IA", "amber"], manual: ["Manual", "muted"] };
const COUNT = (u) => !["kg", "quilo", "kilo", "l", "lt", "litro", "litros", "g", "gr", "ml"].includes((u || "").trim().toLowerCase());

function PrepCard({ p, i, products, adjust, setPrep, onRemove }) {
  const setIng = (j, v) => setPrep({ ...p, ingredients: p.ingredients.map((x, k) => (k === j ? { ...x, ...v, basis: "manual", edited: true } : x)) });
  return (
    <div className="rounded-xl border p-3 space-y-2" data-testid={`plan-prep-${i}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="info">{MEALS[p.meal]}</Badge>
        <span className="font-semibold text-[#0B192C] text-sm">{p.preparation}</span>
        <div className="flex-1" />
        <button title="Remover preparação" data-testid={`plan-prep-remove-${i}`} onClick={onRemove}><Trash2 className="w-4 h-4 text-red-500" /></button>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-sm mapia-table min-w-[620px]">
        <thead><tr><th>Gênero em estoque</th><th className="num">Per capita (g/ml)</th><th className="num">Ajustado</th><th>Origem</th><th /></tr></thead>
        <tbody>
          {p.ingredients.map((g, j) => (
            <tr key={j} data-testid={`plan-ing-${i}-${j}`}>
              <td className="min-w-[220px]"><ProductSelect products={products} value={g.product_id} testid={`plan-ing-product-${i}-${j}`} onChange={(v) => setIng(j, { product_id: v })} /></td>
              <td className="num"><input type="number" step="any" className={`${cellCls} w-24 text-right`} data-testid={`plan-ing-pc-${i}-${j}`} value={g.per_capita_g ?? ""} onChange={(e) => setIng(j, { per_capita_g: num(e.target.value) })} /></td>
              <td className="num text-xs">{adjust[g.product_id] && !g.edited ? <span className="text-[#FF6500] font-semibold">{fmt(g.per_capita_g * adjust[g.product_id])} (×{fmt(adjust[g.product_id])})</span> : "—"}</td>
              <td><Badge tone={(BASIS[g.basis] || BASIS.estimado)[1]}>{(BASIS[g.basis] || BASIS.estimado)[0]}</Badge></td>
              <td><button title="Remover gênero" data-testid={`plan-ing-remove-${i}-${j}`} onClick={() => setPrep({ ...p, ingredients: p.ingredients.filter((_, k) => k !== j) })}><Trash2 className="w-4 h-4 text-red-500" /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <Btn variant="ghost" data-testid={`plan-ing-add-${i}`} onClick={() => setPrep({ ...p, ingredients: [...p.ingredients, { product_id: "", per_capita_g: null, basis: "manual" }] })}><Plus className="w-4 h-4" />Adicionar gênero</Btn>
    </div>
  );
}

function UnitGrams({ plan, setPlan, products }) {
  const used = [...new Set(plan.preparations.flatMap((p) => p.ingredients.map((g) => g.product_id)))];
  const rows = products.filter((p) => used.includes(p.id) && COUNT(p.unit));
  if (!rows.length) return null;
  return (
    <div className="rounded-xl border p-3 space-y-2" data-testid="plan-unit-grams">
      <h4 className="font-heading font-bold text-sm text-[#0B192C]">Peso de 1 unidade (g ou ml)</h4>
      <p className="text-xs text-slate-500">Necessário para converter o per capita em unidades. Sem o peso, o gênero é distribuído igualmente nos dias.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {rows.map((p) => (
          <label key={p.id} className="flex items-center gap-2 text-sm">
            <span className="flex-1 truncate">{p.name} ({p.unit})</span>
            <input type="number" step="any" className={`${cellCls} w-24 text-right`} data-testid={`plan-ug-${p.id}`} value={plan.unit_grams[p.id] ?? ""} onChange={(e) => setPlan({ ...plan, unit_grams: { ...plan.unit_grams, [p.id]: num(e.target.value) } })} />
          </label>
        ))}
      </div>
    </div>
  );
}

export default function PlanEditor({ initial, adjust, onClose, onSaved }) {
  const { products, month } = useSchool();
  const [plan, setPlan] = useState({ preparations: initial.preparations, unit_grams: { ...initial.unit_grams } });
  const [busy, setBusy] = useState(false);
  const invalid = plan.preparations.some((p) => p.ingredients.some((g) => !g.product_id || !(g.per_capita_g > 0)));
  const save = async () => {
    setBusy(true);
    const unit_grams = Object.fromEntries(Object.entries(plan.unit_grams).filter(([, v]) => v > 0));
    const preparations = plan.preparations.filter((p) => p.ingredients.length).map((p) => ({ ...p, ingredients: p.ingredients.map(({ edited, ...g }) => g) }));
    const ok = await run(() => api.put(`/s/months/${month}/plan`, { preparations, unit_grams }), "Distribuição salva — controles recalculados");
    setBusy(false);
    if (ok) onSaved();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-[1100px] w-[96vw] max-h-[92vh] overflow-y-auto" data-testid="plan-editor-dialog">
        <DialogHeader><DialogTitle>Distribuição per capita por preparação</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-600">Confira os gêneros e o per capita escolhidos pela IA para cada preparação do cardápio. A coluna "Ajustado" mostra o per capita aumentado automaticamente para usar a sobra do mês, sem passar do estoque comprado. Ao salvar, sua versão fica valendo e não é substituída automaticamente; use "Refazer distribuição" para voltar à sugestão da IA.</p>
        <div className="space-y-3">
          {plan.preparations.map((p, i) => (
            <PrepCard key={i} p={p} i={i} products={products || []} adjust={adjust}
              setPrep={(v) => setPlan({ ...plan, preparations: plan.preparations.map((x, k) => (k === i ? v : x)) })}
              onRemove={() => setPlan({ ...plan, preparations: plan.preparations.filter((_, k) => k !== i) })} />
          ))}
          <UnitGrams plan={plan} setPlan={setPlan} products={products || []} />
        </div>
        {invalid && <div className="text-sm text-red-600" data-testid="plan-editor-invalid">Escolha o gênero e informe um per capita maior que zero em todas as linhas.</div>}
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" data-testid="plan-editor-cancel" onClick={onClose}>Cancelar</Btn>
          <Btn data-testid="plan-editor-save" disabled={busy || invalid} onClick={save}><Save className="w-4 h-4" />{busy ? "Salvando…" : "Salvar distribuição"}</Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
}
